/**
 * client/camera/bob.ts — view and weapon bob factors (pure, no THREE).
 *
 * OWNER: VIEWMODEL+CAMERA agent.
 * Port of T5 bgame/bg_weapons_view.cpp: BG_GetBobCycle, BG_CalculateViewMovement_{Vertical,Horizontal}BobFactor,
 * BG_CalculateWeaponMovement_{Vertical,Horizontal}BobFactor [MV §1.5]. Amplitudes are T5 dvar defaults
 * (ESTIMATE for T6, see dvars.ts). Our flag mapping: T5 pm_flags 0x8000 sprint = PMF.SPRINTING (same bit),
 * T5 0x400000 dive-to-prone = our PMF.DIVE.
 */
import { PMF } from '@shared/types';
import {
  BG_VIEW_BOB_DTP, BG_VIEW_BOB_DUCKED, BG_VIEW_BOB_DUCKED_ADS, BG_VIEW_BOB_MAX, BG_VIEW_BOB_PRONE, BG_VIEW_BOB_SPRINTING,
  BG_VIEW_BOB_STANDING, BG_VIEW_BOB_STANDING_ADS, BG_VIEW_BOB_SWIMMING, BG_WEAPON_BOB_DTP, BG_WEAPON_BOB_DUCKED,
  BG_WEAPON_BOB_MAX, BG_WEAPON_BOB_PRONE, BG_WEAPON_BOB_SPRINTING, BG_WEAPON_BOB_STANDING, BG_WEAPON_BOB_SWIMMING,
} from './dvars';

/** The PlayerState fields bob needs. */
export interface BobPs {
  bobCycle: number;
  pm_flags: number;
  viewHeightTarget: number;
  waterlevel: number;
  /** fWeaponPosFrac. */
  adsFrac: number;
}

/** Per-weapon bob multipliers (weapon file sprintBobH/V, dtpBobH/V). */
export interface WeaponBobDef {
  sprintBob: readonly [number, number];
  dtpBob: readonly [number, number];
}

/**
 * BG_GetBobCycle: (u8 bobCycle / 255) * 2π + 2π. Accepts a fractional render-rate bobCycle (psview PredictedLerp);
 * for integers this is exactly `bobCycle & 255`.
 */
export function getBobCycle(bobCycle: number): number {
  const c = (((bobCycle % 256) + 256) % 256) / 255;
  return c * Math.PI + c * Math.PI + 2 * Math.PI;
}

/** axis 0 = horizontal (vector[0]), 1 = vertical (vector[1]). */
function viewAmplitude(ps: BobPs, speed: number, axis: 0 | 1): number {
  let a: number;
  if (ps.waterlevel < 3) {
    if (ps.pm_flags & PMF.DIVE) a = speed * BG_VIEW_BOB_DTP[axis];
    else if (ps.viewHeightTarget === 11) a = speed * BG_VIEW_BOB_PRONE[axis];
    else if (ps.viewHeightTarget === 40) a = ((1 - ps.adsFrac) * BG_VIEW_BOB_DUCKED[axis] + ps.adsFrac * BG_VIEW_BOB_DUCKED_ADS[axis]) * speed;
    else if (ps.pm_flags & PMF.SPRINTING) a = speed * BG_VIEW_BOB_SPRINTING[axis];
    else a = ((1 - ps.adsFrac) * BG_VIEW_BOB_STANDING[axis] + ps.adsFrac * BG_VIEW_BOB_STANDING_ADS[axis]) * speed;
  } else {
    a = BG_VIEW_BOB_SWIMMING[axis];
  }
  return a > BG_VIEW_BOB_MAX ? BG_VIEW_BOB_MAX : a;
}

/** BG_CalculateViewMovement_VerticalBobFactor (inches added to the eye z). */
export function viewBobVertical(ps: BobPs, cycle: number, speed: number): number {
  const amp = viewAmplitude(ps, speed, 1);
  return (Math.sin(cycle * 2) + Math.sin(cycle * 4 + Math.PI / 2) * 0.2) * 0.75 * amp;
}

/** BG_CalculateViewMovement_HorizontalBobFactor (inches along view right). */
export function viewBobHorizontal(ps: BobPs, cycle: number, speed: number): number {
  return Math.sin(cycle) * viewAmplitude(ps, speed, 0);
}

function weaponAmplitude(ps: BobPs, def: WeaponBobDef, speed: number, axis: 0 | 1): number {
  let a: number;
  if (ps.waterlevel < 3) {
    if (ps.pm_flags & PMF.DIVE) a = speed * BG_WEAPON_BOB_DTP[axis] * def.dtpBob[axis];
    else if (ps.viewHeightTarget === 11) a = speed * BG_WEAPON_BOB_PRONE[axis];
    else if (ps.viewHeightTarget === 40) a = (1 - ps.adsFrac) * speed * BG_WEAPON_BOB_DUCKED[axis];
    else if (ps.pm_flags & PMF.SPRINTING) a = speed * BG_WEAPON_BOB_SPRINTING[axis] * def.sprintBob[axis];
    else a = (1 - ps.adsFrac) * speed * BG_WEAPON_BOB_STANDING[axis];
  } else {
    a = BG_WEAPON_BOB_SWIMMING[axis];
  }
  return a > BG_WEAPON_BOB_MAX ? BG_WEAPON_BOB_MAX : a;
}

/** BG_CalculateWeaponMovement_VerticalBobFactor (degrees). */
export function weaponBobVertical(ps: BobPs, def: WeaponBobDef, cycle: number, speed: number): number {
  const amp = weaponAmplitude(ps, def, speed, 1);
  return (Math.sin(cycle * 2) + Math.sin(cycle * 4 + Math.PI / 2) * 0.2) * 0.75 * amp;
}

/** BG_CalculateWeaponMovement_HorizontalBobFactor (degrees). */
export function weaponBobHorizontal(ps: BobPs, def: WeaponBobDef, cycle: number, speed: number): number {
  return Math.sin(cycle) * weaponAmplitude(ps, def, speed, 0);
}
